import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {isEmpty} from 'lodash';
import * as jmespath from 'jmespath';
import {filter, takeUntil, switchMap, tap, map} from 'rxjs/operators';
import {TestService} from '@ispm/dashboard/test/services/test.service';
import {WorkflowService} from '@ispm/workflow/services/workflow.service';
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {Subject} from 'rxjs/Subject';
import {BehaviorSubject, of} from "rxjs";
import {ISubjectsAppearance} from "@ispm/therapies/interfaces/subject.interface";
import {combineLatest} from "rxjs/observable/combineLatest";
import {ActivatedRoute} from "@angular/router";
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import {TranslateService} from '@ngx-translate/core';


@Component({
  selector: 'ispm-subject-list-cell',
  templateUrl: './subject-list-cell.component.html',
  styleUrls: ['./subject-list-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubjectListCellComponent implements OnInit, OnDestroy {
  @HostBinding('class')
  private hostClasses;

  @Input()
  public field: any;

  @Input()
  public editVisible: boolean;

  @Input()
  public editable: boolean;

  @Input()
  public selected: boolean;

  @Input()
  subject: ISubjectsAppearance['subjects'][keyof ISubjectsAppearance['subjects']];

  @Input()
  subjectsAppearance: ISubjectsAppearance;

  @Input()
  public applyEllipsis: boolean = false;

  @Input()
  public isMda: boolean;

  @Input()
  public previewMode: boolean;

  @Input()
  public set value(value) {
    this.applyEllipsis = true;

    this.inputValue$.next(value);
  }

  public get value() {
    return this._value;
  }

  @Output()
  public editTherapyClicked: EventEmitter<void> = new EventEmitter();

  @Output()
  public includeInStatusChange: EventEmitter<{ includeIn: 'report' | 'MI', state: boolean}> = new EventEmitter();

  public ellipsisApplied: boolean;
  public tooltipStatus: boolean = false;
  public includeInReportDisabled: boolean = false;
  public tooltip: string;
  public icon: string;
  public  tooltipArr :any;


  private destroyed$: Subject<any> = new Subject();
  private inputValue$: BehaviorSubject<any> = new BehaviorSubject(null);
  private _value: any;

  private isIE = /MSIE|Trident/.test(window.navigator.userAgent);

  constructor(
    private _cd: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private therapiesAC: TherapiesActionCreatorService,
    private authService: AuthenticationService,
    private testService: TestService,
    private workflowService: WorkflowService,
    private translateService: TranslateService
  ) { }

  public ngOnInit() {
    this.hostClasses = this.field?.cssClasses  ? this.field?.cssClasses  : '';

    switch (this.field?.type) {
      case 'include_in_report':
      case 'counter':
        this.hostClasses += ' small-sizing';
        break;
      case 'textual_content':
          this.hostClasses += ' textual_content';
          break;
      case 'include_in_report_toggle':
        this.hostClasses += this.field?.cellCssClasses;
        break;
      default:
        break;
    }

    this.inputValue$.pipe(
      filter(inputValue => !!inputValue),
      switchMap((inputValue) => {

        this._value = isEmpty(this.field.path) ? inputValue : jmespath.search(inputValue, this.field.path);
        this._cd.markForCheck();
        return of(null);
      }),
      takeUntil(this.destroyed$.asObservable()),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public updateTooltipStatus(status: boolean) {
    this.tooltipStatus = status;
    this._cd.markForCheck();
  }

  public openEditTherapy() {
    this.editTherapyClicked.emit();
  }

  public changeIncludeIn(includeIn: 'report' | 'MI', event: Event) {
    if (event.stopPropagation ) {
      event.stopPropagation();
    }

    if (!this.includeInReportDisabled) {
      this.includeInStatusChange.emit({ includeIn, state: !this.subject.visible[includeIn]});
    }
  }

  public ellipsisApplyChange(state: boolean) {
    this.ellipsisApplied = state;
  }

  public shouldApplyEllipsis() {
    if (this.ellipsisApplied ||  this.isIE ) {
      return false;
    }

    return this.applyEllipsis;
  }

  public isEditDisabled() {
    return !(this.editable && (this.field.conf ? this.field.conf.disableNotSelected && this.selected : true));
  }

  public isUserCanChooseAberrationToMI(){
    if (!this.testService) {
      return;
    }
    const curationEnabled =this.testService.testConfiguration?this.testService.testConfiguration['AllowSendToCurationUserList']:undefined;
    if (curationEnabled && curationEnabled.length > 0 && this.workflowService.currentTest.workflowRequest.activeJobName == "UiManualAberrationCuration" )
      return !(curationEnabled && curationEnabled.indexOf(this.authService.user.info.id) > -1 );
    return true;
  }

  public MolecularFindingResource(){
    if(this._value.subjects && this._value.subjects.length>0 &&
      this._value.subjects[0].patientData?.patientBiomarkers &&
      this._value.subjects[0].patientData?.patientBiomarkers.length == 1 &&
      this._value.subjects[0].patientData?.patientBiomarkers[0].AberrationClass == 'MolecularFinding')
      return true;
    return false;
  }

  public displayIcon(){
    this.tooltipArr = this.field?.path?.split(".");
    this.tooltip = this.translateService.instant('therapyMatch.' + this.tooltipArr[this.tooltipArr.length-1]);
    this.icon = this.field?.path == 'autoEdited' ? 'e' : '!';
    return (this.icon == 'e' && this._value == "true") || (this.icon == '!' && !this._value) ? true : false;
  }
}
